export const emptyProfile = {
  personal: {
    full_name: "", phone: "", age: "", occupation: "", city: "",
    marital_status: "", dependents: "", year_of_planning: new Date().getFullYear(),
  },
  family: {
    spouse: { name: "", age: "", occupation: "" },
    children: [], // {name, age}
  },
  retirement: {
    current_age: "", retirement_year: "", retirement_age: "", life_expectancy: "",
    post_retirement_income: "", monthly_expense_today: "",
  },
  income: {
    // Monthly (post-tax)
    salary_self: "", pf_self: "", salary_spouse: "", pf_spouse: "",
    business_income: "", rent_received: "", interest_received: "",
    dividend_received: "", other_income: "",
    // Yearly
    bonus_self: "", bonus_spouse: "", business_yearly: "", other_yearly: "",
  },
  expenses: {
    housing: [],         // [{name, amount, frequency}]
    personal_food: [],
    health_family: [],
    transport_travel: [],
    recreation_misc: [],
  },
  financial_assets: {
    savings_bank: "", current_account: "", fixed_deposits: "",
    epf_self: "", epf_spouse: "", ppf: "", nps: "",
    direct_equity: "", esops_rsus: "",
    mutual_funds_equity: "", mutual_funds_debt: "",
    bonds_debentures: "", pomis_nsc_kvp: "",
    sovereign_gold_bonds: "", gold_coins_bars: "", silver: "",
    rent_deposits_paid: "", crypto: "", sukanya_samriddhi: "",
    insurance_policies_cv: "", gratuity: "", other_financial: "",
  },
  fixed_assets: {
    own_house: "", vacation_house: "", vehicles: "",
    gold_jewellery: "", recreational_equipment: "",
    household_furnishings: "", collectibles: "",
    investment_real_estate: "", land: "", other_fixed: "",
  },
  liabilities_table: [],
  // {type, lender, start_year, end_year, monthly_emi, interest_rate, outstanding}
  investments_monthly: {
    recurring_deposit: "", ppf: "", epf: "", sip_mutual_funds: "",
    gold_scheme: "", nps: "", endowment_moneyback: "", ulip: "",
    pomis_nsc_kvp: "", esops_espp: "", sukanya_samriddhi: "", other: "",
  },
  investments_yearly: {
    ppf: "", nps: "", insurance_premium: "",
    sukanya_samriddhi: "", elss: "", other: "",
  },
  insurance: {
    has_term_insurance: false, term_cover_amount: "", term_premium: "",
    has_health_insurance: false, health_cover_amount: "", health_premium: "",
    has_employer_health: false, employer_health_cover: "",
  },
  goals: [],
  // {name, today_cost, target_year, inflation, comment}
  financial_notes: "",
  submitted: false,
};

export const STEPS = [
  { key: "family", label: "Family" },
  { key: "income", label: "Income" },
  { key: "expenses", label: "Expenses" },
  { key: "assets", label: "Assets" },
  { key: "liabilities", label: "Loans" },
  { key: "investments", label: "Investments" },
  { key: "insurance", label: "Insurance" },
  { key: "goals", label: "Goals" },
];

export const fmtINR = (n) => new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(Number(n || 0));

const num = (x) => Number(x || 0);

export const summarize = (p) => {
  const inc = p?.income || {};
  const insMonthly = ["salary_self","salary_spouse","business_income","rent_received","interest_received","dividend_received","other_income"]
    .reduce((s,k)=>s+num(inc[k]),0);
  const insYearly = ["bonus_self","bonus_spouse","business_yearly","other_yearly"]
    .reduce((s,k)=>s+num(inc[k]),0);
  const totalFinancial = Object.values(p?.financial_assets || {}).reduce((s,v)=>s+num(v),0);
  const totalFixed = Object.values(p?.fixed_assets || {}).reduce((s,v)=>s+num(v),0);
  const totalAssets = totalFinancial + totalFixed;
  const liab = p?.liabilities_table || [];
  const totalEmi = liab.reduce((s,l)=>s+num(l.monthly_emi),0);
  const totalLiab = liab.reduce((s,l)=>s+num(l.outstanding),0);
  const monthlySip = Object.values(p?.investments_monthly || {}).reduce((s,v)=>s+num(v),0);
  const yearlyInv = Object.values(p?.investments_yearly || {}).reduce((s,v)=>s+num(v),0);
  // Expenses
  let monthlyExpense = 0, annualExpense = 0;
  Object.values(p?.expenses || {}).forEach((arr) => {
    (arr || []).forEach((it) => {
      const amt = num(it.amount);
      const freq = (it.frequency || "Monthly").toLowerCase();
      if (freq === "monthly") { monthlyExpense += amt; annualExpense += amt * 12; }
      else { annualExpense += amt; monthlyExpense += amt / 12; }
    });
  });
  const netWorth = totalAssets - totalLiab;
  const emiRatio = insMonthly ? (totalEmi / insMonthly) * 100 : 0;
  const savingsRate = insMonthly ? ((insMonthly - monthlyExpense - totalEmi) / insMonthly) * 100 : 0;
  const termTarget = insMonthly * 12 * 15;
  const termGap = Math.max(0, termTarget - num(p?.insurance?.term_cover_amount));
  return {
    monthlyInflow: insMonthly, yearlyInflow: insYearly,
    monthlyExpense, annualExpense,
    totalFinancial, totalFixed, totalAssets,
    totalLiab, netWorth, totalEmi, emiRatio,
    monthlySip, yearlyInv, savingsRate,
    termTarget, termGap,
  };
};

export const DEFAULT_EXPENSE_TEMPLATES = {
  housing: [
    "House Rent", "Apartment Maintenance Charges", "Property Tax", "Electricity",
    "Water", "Phone Bills", "Cooking Gas", "Househelp (Maids, cook, Driver, Nanny)",
    "Internet", "Household Gadgets & Appliances", "House Repairs",
  ],
  personal_food: [
    "Groceries", "Eating Out (Essential)", "Clothes, Footwear & Accessories",
    "Beauty / Salon / Grooming",
  ],
  health_family: [
    "Medicines", "Doctor Consultation", "Regular lab tests", "Dentist / dental",
    "Child Education Fees", "Child Care / day-care", "Monetary Support of Parent or Child",
  ],
  transport_travel: [
    "Petrol / Diesel", "Vehicle Maintenance & Repair",
    "Short Vacations", "Long Vacations", "Family Visits",
  ],
  recreation_misc: [
    "Gym / Health Club", "Movies & Sports Events", "Dining Out (Discretionary)",
    "Charitable Contributions", "Gifts", "Newspaper / Subscriptions", "Other",
  ],
};

export const EXPENSE_GROUPS = [
  { key: "housing", label: "Housing & Utilities" },
  { key: "personal_food", label: "Personal & Food" },
  { key: "health_family", label: "Health & Family Care" },
  { key: "transport_travel", label: "Transport & Travel" },
  { key: "recreation_misc", label: "Recreation & Misc" },
];

export const LOAN_TYPES = [
  "Housing Loan", "Auto Loan", "Mortgage Loan", "Personal Loan",
  "Credit Card Loan", "Education Loan", "Other Loan",
];

export const GOAL_PRESETS = [
  "Retirement", "Children Graduation", "Children Marriage",
  "House (Own Use)", "House (Investment)", "Vacation", "Car", "Custom",
];
